import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase/client'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { feedbackKeys } from './useAdminFeedback'

/**
 * Subscribe to Supabase Realtime for feedback updates.
 * Invalidates React Query caches on new messages or conversation changes.
 */
export function useAdminFeedbackRealtime() {
  const qc = useQueryClient()
  const { user } = useAdminAuth()

  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('admin-feedback-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const msg = payload.new as { conversation_id: string; sender_role: string; body?: string }
          // Invalidate detail for this conversation
          qc.invalidateQueries({ queryKey: feedbackKeys.detail(msg.conversation_id) })
          // Invalidate list
          qc.invalidateQueries({ queryKey: feedbackKeys.all })
          // Show toast for user messages (not our own admin replies)
          if (msg.sender_role === 'user') {
            toast.info('New feedback message', {
              description: msg.body?.slice(0, 80) ?? 'New attachment',
            })
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'conversations' },
        () => {
          qc.invalidateQueries({ queryKey: feedbackKeys.all })
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, qc])
}
